<template>
  <v-card outlined class="mb-4 rounded-lg">
    <v-card-text class="pb-3">

      <!-- Row 1: Avatar + name + roles below -->
      <div class="d-flex align-center mb-4 pa-3 rounded-lg" style="background:#f5f5f5;border:1px solid #e0e0e0;gap:12px;">
        <v-avatar size="44" color="primary" class="flex-shrink-0">
          <span class="white--text subtitle-2 font-weight-black">{{ initials }}</span>
        </v-avatar>
        <div style="min-width:0;">
          <div class="subtitle-1 font-weight-black text-truncate">{{ displayName }}</div>
          <div class="d-flex flex-wrap align-center mt-1" style="gap:4px;">
            <span v-if="roleDetails.length === 0" class="caption grey--text">No roles assigned.</span>
            <template v-else>
              <div
                v-for="r in roleDetails"
                :key="r.key"
                class="d-inline-flex align-center"
                style="gap:3px;"
              >
                <v-chip
                  x-small
                  :color="r.color"
                  :dark="r.dark"
                  :outlined="!r.dark"
                  class="font-weight-medium"
                >
                  <v-icon x-small class="mr-1">{{ r.icon }}</v-icon>
                  {{ r.label }}
                </v-chip>
                <v-chip
                  v-if="r.isManager"
                  x-small
                  color="amber darken-2"
                  dark
                  class="font-weight-bold"
                  title="You manage this role"
                >
                  <v-icon x-small class="mr-1">mdi-account-tie</v-icon>
                  Mgr
                </v-chip>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Row 2: My Stats header + Level -->
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <v-icon small color="amber darken-1" class="mr-2">mdi-trophy</v-icon>
          <span class="subtitle-2 font-weight-bold">My Stats</span>
        </div>
        <div class="d-flex align-center" style="gap:6px;">
          <span class="caption grey--text text-uppercase font-weight-medium">Level</span>
          <v-chip x-small outlined color="grey darken-2" class="font-weight-black">{{ snap.levelName }}</v-chip>
          <v-chip v-if="snap.multiplier > 1" x-small color="amber lighten-4" class="amber--text text--darken-3 font-weight-medium">
            <v-icon x-small class="mr-1">mdi-fire</v-icon>×{{ snap.multiplier.toFixed(1) }}
          </v-chip>
        </div>
      </div>

      <!-- Row 3: KPI / Tasks / AI / Habits stats -->
      <v-row dense class="mb-3">
        <v-col v-for="cat in categoryStats" :key="cat.label" cols="6" sm="3">
          <div class="d-flex align-center justify-space-between pa-2 rounded-lg" style="background:#f5f5f5;border:1px solid #e0e0e0;">
            <div class="d-flex align-center">
              <v-icon small :color="cat.color" class="mr-1">{{ cat.icon }}</v-icon>
              <span class="body-2 grey--text">{{ cat.label }}</span>
            </div>
            <span class="subtitle-2 font-weight-black">{{ cat.points }}</span>
          </div>
        </v-col>
      </v-row>

      <!-- Row 4: Today / Total pts + pts to next level -->
      <div class="d-flex align-center justify-space-between caption grey--text mb-2">
        <span>Today: <strong class="black--text">{{ snap.todayPoints }} pts</strong> &nbsp; Total: <strong class="black--text">{{ snap.totalPoints }} pts</strong></span>
        <span v-if="snap.nextLevelName">{{ snap.pointsToNext }} pts to <strong class="black--text">{{ snap.nextLevelName }}</strong></span>
      </div>
      <v-progress-linear v-if="snap.nextLevelName" :value="snap.levelProgress * 100" color="primary" rounded height="6" background-color="#e0e0e0" />
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'RoleProfileCard',
  inject: {
    roles: { default: () => () => [] },
    allManagers: { default: () => () => [] },
  },
  data() {
    return {
      loading: false,
      saving: false,
      profile: { name: '', role: '', department: '', is_manager: false },
      snap: {
        levelName: '…', multiplier: 1,
        nextLevelName: null, pointsToNext: null, levelProgress: 0,
        todayPoints: 0, totalPoints: 0,
      },
      categoryStats: [
        { label: 'KPI',    icon: 'mdi-target',             color: 'primary',      points: 0 },
        { label: 'Tasks',  icon: 'mdi-format-list-checks', color: 'amber darken-1', points: 0 },
        { label: 'AI',     icon: 'mdi-lightning-bolt',     color: 'blue',         points: 0 },
        { label: 'Habits', icon: 'mdi-repeat',             color: 'green',        points: 0 },
      ],
    };
  },
  computed: {
    storeUser() {
      return this.$store.state.auth?.user || {};
    },
    displayName() {
      return this.profile.name || this.storeUser.name || 'You';
    },
    initials() {
      const n = this.displayName;
      const parts = n.trim().split(' ').filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return n.slice(0, 2).toUpperCase();
    },
    roleLabel() {
      const rid = this.storeUser.role_id;
      if (rid === 1 || rid === 11) return 'Admin';
      if (rid === 2) return 'Manager';
      return 'Employee';
    },
    roleColor() {
      const rid = this.storeUser.role_id;
      if (rid === 1 || rid === 11) return 'deep-purple';
      if (rid === 2) return 'blue darken-1';
      return 'grey darken-1';
    },
    userRoleMembers() {
      // Pull role assignments for the current user (multi-role support).
      return Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
    },
    managerAssignments() {
      // Full user_role_members table (who is a manager of which role) — injected from DefaultLayout.
      const list = typeof this.allManagers === 'function' ? this.allManagers() : (this.allManagers || []);
      return Array.isArray(list) ? list : [];
    },
    allRoles() {
      const list = typeof this.roles === 'function' ? this.roles() : (this.roles || []);
      return Array.isArray(list) ? list : [];
    },
    isManagerOfRole() {
      // Returns a function: (roleId) => boolean — true if the current user is a manager of that role.
      const uid = this.storeUser.id;
      const assignments = this.managerAssignments;
      return (roleId) => assignments.some(m =>
        m.user_id === uid && m.user_role_id === roleId && (m.is_manager === true || m.is_manager === 1 || m.is_manager === undefined)
      );
    },
    isManager() {
      // Any form of manager: legacy role_id or manager on at least one role.
      const rid = this.storeUser.role_id;
      if (rid === 1 || rid === 2 || rid === 11) return true;
      const uid = this.storeUser.id;
      return this.managerAssignments.some(m => m.user_id === uid);
    },
    roleDetails() {
      // Preferred path: multi-role via role_members. Each entry becomes a chip; we flag manager roles.
      if (this.userRoleMembers.length > 0) {
        const items = this.userRoleMembers.map((m) => {
          const roleId = m.user_role_id ?? m.role?.id;
          const fallbackRole = this.allRoles.find(r => r.id === roleId);
          const name = m.role?.name || fallbackRole?.name || `Role #${roleId}`;
          const isMgr = this.isManagerOfRole(roleId);
          return {
            key: `role-${roleId}`,
            label: name,
            icon: isMgr ? 'mdi-account-tie' : 'mdi-account-group',
            color: isMgr ? 'blue darken-1' : 'grey darken-1',
            dark: isMgr,
            isManager: isMgr,
          };
        });
        // Keep manager roles first for visibility.
        return items.sort((a, b) => (b.isManager === true) - (a.isManager === true));
      }

      // Legacy fallback: use role_id if no role_members are present.
      const rid = this.storeUser.role_id;
      if (rid === 1 || rid === 11) {
        return [{ key: 'legacy-admin', label: 'Admin', icon: 'mdi-shield-crown', color: 'deep-purple', dark: true, isManager: true }];
      }
      if (rid === 2) {
        return [{ key: 'legacy-manager', label: 'Manager', icon: 'mdi-account-tie', color: 'blue darken-1', dark: true, isManager: true }];
      }
      return [{ key: 'legacy-employee', label: 'Employee', icon: 'mdi-account', color: 'grey darken-1', dark: false, isManager: false }];
    },
  },
  mounted() {
    this.load();
    this.$root.$on('growth:refresh', this.load);
  },
  beforeDestroy() {
    this.$root.$off('growth:refresh', this.load);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        // Primary source: /profile returns { profile, snap }.
        const res = await this.$axios.get('api/employee-growth/profile');
        // eslint-disable-next-line no-console
        console.log('[RoleProfileCard] /profile raw response:', res && res.data);
        if (res && res.data && res.data.response && res.data.data) {
          const { profile, snap } = res.data.data;
          // eslint-disable-next-line no-console
          console.log('[RoleProfileCard] extracted snap:', snap);
          if (profile) {
            this.profile = {
              name: profile.name || '',
              role: profile.role || '',
              department: profile.department || '',
              is_manager: !!profile.is_manager,
            };
          }
          if (snap) {
            this.applySnap(snap);
            // eslint-disable-next-line no-console
            console.log('[RoleProfileCard] after applySnap, categoryStats:', JSON.parse(JSON.stringify(this.categoryStats)));
            return;
          }
        }
        // Fallback: if /profile failed or did not include snap, fetch /score directly.
        // eslint-disable-next-line no-console
        console.warn('[RoleProfileCard] /profile did not contain snap, falling back');
        await this.loadScoreFallback();
      } catch (e) {
        // Network/server error — try the score endpoint as a backup so stats still render.
        // eslint-disable-next-line no-console
        console.warn('[RoleProfileCard] /profile failed, falling back to /score', e);
        await this.loadScoreFallback();
      } finally {
        this.loading = false;
      }
    },
    async loadScoreFallback() {
      try {
        const res = await this.$axios.get('api/employee-growth/score');
        if (res && res.data && res.data.response && res.data.data) {
          this.applySnap(res.data.data);
        }
      } catch (e) { /* silently fail — stats remain at 0 */ }
    },
    applySnap(snap) {
      // Normalize snake_case payload → camelCase view model.
      this.snap = {
        levelName: snap.level_name || snap.levelName || '…',
        multiplier: snap.multiplier || 1,
        nextLevelName: snap.next_level_name || snap.nextLevelName || null,
        pointsToNext: snap.points_to_next ?? snap.pointsToNext ?? null,
        levelProgress: snap.level_progress ?? snap.levelProgress ?? 0,
        todayPoints: snap.today_points ?? snap.todayPoints ?? 0,
        totalPoints: snap.total_points ?? snap.totalPoints ?? 0,
      };
      // Use cumulative all-time points from by_pillar_pts (not normalized 0-100 scores).
      const byPillar = snap.by_pillar_pts || snap.pillar_scores || {};
      const kpi    = byPillar.kpi   ?? 0;
      const task   = byPillar.task  ?? byPillar.tasks  ?? 0;
      const ai     = byPillar.ai    ?? 0;
      const habit  = byPillar.habit ?? byPillar.habits ?? 0;
      const next = [
        { ...this.categoryStats[0], points: Number(kpi)   || 0 },
        { ...this.categoryStats[1], points: Number(task)  || 0 },
        { ...this.categoryStats[2], points: Number(ai)    || 0 },
        { ...this.categoryStats[3], points: Number(habit) || 0 },
      ];
      this.categoryStats = next;
    },
    async saveProfile() {
      this.saving = true;
      try {
        const res = await this.$axios.post('api/employee-growth/profile', this.profile);
        if (res.data.response) {
          this.$toast && this.$toast.success('Profile saved!');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
